/**
 * NPEFixationFormDialog — Create/Edit NPE Fixation (51 fields, SOP §7.7 Table 58).
 * Tabs:
 *   - Fixation (header)  : npe_no autocomplete, annex_index (Auto), fixation_date,
 *                          fixation_price, note
 *   - LocalP New (5 tabs) : tái sử dụng GeneralTab/PricingTab/LogisticsTab/
 *                           ForwardTab/IntermediaryTab/FlagsNotesTab của LocalPFormDialog
 *
 * Workflow:
 *   - Khi user chọn `npe_no`, tự động fetch NPE contract -> auto-fill các field
 *     LocalP (cp_code, quality_code, certification, latest_delivery, package,
 *     delivery_place_code, trader_in_charge, shipper_code, quantity_kg,
 *     stoploss_rate, provi_market_rate, ...). User có thể override.
 *   - 2 nút Save:
 *       * "Save" -> POST /create  (tạo cs_fixation + local_p)
 *       * "Save & Allocate" -> POST /create-and-allocate -> navigate
 *         /purchase/npe-fixations/{id}/allocate?session={uid}
 */
import { yupResolver } from '@hookform/resolvers/yup'
import {
  Autocomplete,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Tab,
  Tabs,
  TextField,
} from '@mui/material'
import { DatePicker } from '@mui/x-date-pickers/DatePicker'
import dayjs from 'dayjs'
import { useEffect, useState } from 'react'
import { Controller, useForm, type Control } from 'react-hook-form'
import { useNavigate } from 'react-router-dom'
import * as yup from 'yup'

import { useSnackbar } from '@/hooks/useSnackbar'
import { useListNPEContractsQuery } from '@/store/api/npeContractsApi'
import {
  useCreateAndAllocateNPEFixationMutation,
  useCreateNPEFixationMutation,
  useUpdateNPEFixationMutation,
} from '@/store/api/npeFixationsApi'
import type {
  LocalPCreateIn,
  NPEContractOut,
  NPEFixationCreateIn,
  NPEFixationOut,
} from '@/types/api'
import { getErrorMessage } from '@/utils/error'

import GeneralTab from './localp-form-tabs/GeneralTab'
import PricingTab from './localp-form-tabs/PricingTab'
import LogisticsTab from './localp-form-tabs/LogisticsTab'
import ForwardTab from './localp-form-tabs/ForwardTab'
import IntermediaryTab from './localp-form-tabs/IntermediaryTab'
import FlagsNotesTab from './localp-form-tabs/FlagsNotesTab'
import { EMPTY_LOCALP, type LocalPFormValues } from './localp-form-tabs/types'

/** Merged form values: fixation header + tất cả field LocalPFormValues. */
interface NPEFixationFormValues extends LocalPFormValues {
  // Fixation header
  npe_no: string
  fixation_date: string
  fixation_price: number | ''
  fixation_note: string
}

const EMPTY_FIX: NPEFixationFormValues = {
  ...EMPTY_LOCALP,
  // Fixation đã chốt giá -> luôn Outright
  contract_type: 'Outright',
  npe_no: '',
  fixation_date: dayjs().format('YYYY-MM-DD'),
  fixation_price: '',
  fixation_note: '',
}

// Re-use LocalP schema chỉ cho phần local_p; ở đây ta validate đơn giản hơn
// vì nhiều field auto từ NPE; chỉ ép tối thiểu.
const schema: yup.ObjectSchema<NPEFixationFormValues> = yup.object({
  // ---- Fixation header ----
  npe_no: yup.string().trim().required('Bắt buộc chọn NPE Contract'),
  fixation_date: yup.string().required('Bắt buộc nhập ngày fix'),
  fixation_price: yup
    .mixed<number | ''>()
    .test('required', 'Bắt buộc nhập giá fix', (v) => v !== '' && v !== undefined && v !== null)
    .test('positive', 'Giá fix phải > 0', (v) => typeof v === 'number' && v > 0)
    .required(),
  fixation_note: yup.string().default(''),

  // ---- LocalP — relax (nhiều field auto-fill từ NPE) ----
  contract_no: yup.string().trim().default('').max(20),
  contract_date: yup.string().required('Bắt buộc nhập ngày HĐ Local P'),
  cp_code: yup.string().trim().required('Bắt buộc có vendor (auto từ NPE)'),
  seller_ref: yup.string().trim().default(''),
  quality_code: yup.string().trim().default(''),
  quality_des_contract: yup.string().trim().default(''),
  certification: yup.string().trim().default(''),
  me_or_ib: yup.mixed<'ME' | 'IB' | ''>().oneOf(['ME', 'IB', ''] as const).default(''),
  license_cert_no: yup.string().trim().default(''),
  quantity_kg: yup
    .mixed<number | ''>()
    .test('required', 'Bắt buộc nhập quantity', (v) => v !== '' && v !== undefined && v !== null)
    .test('positive', 'Quantity phải > 0', (v) => typeof v === 'number' && v > 0)
    .required(),
  no_of_bag: yup.mixed<number | ''>().default(''),
  package: yup.string().trim().default(''),

  contract_type: yup
    .mixed<'Outright' | 'Differential'>()
    .oneOf(['Outright', 'Differential'] as const)
    .required(),
  outright_price: yup.mixed<number | ''>().default(''),
  ptbf_diffs: yup.mixed<number | ''>().default(''),
  price_unit: yup.string().trim().default(''),
  vat_rate: yup.mixed<number | ''>().default(''),
  fx: yup.mixed<number | ''>().default(''),
  fixed_fx: yup.mixed<number | ''>().default(''),
  futures_month: yup.string().trim().default(''),
  fixation_call: yup.string().trim().default(''),

  latest_delivery: yup.string().default(''),
  delivery_term: yup.string().trim().default(''),
  delivery_place_code: yup.string().trim().default(''),
  logistic_cost: yup.mixed<number | ''>().default(''),
  trader_in_charge: yup.string().trim().default(''),

  provi_market_rate: yup.mixed<number | ''>().default(''),
  stoploss_rate: yup.mixed<number | ''>().default(''),
  margin_value_per: yup.mixed<number | ''>().default(''),
  margin_limit_per: yup.mixed<number | ''>().default(''),
  time_limit: yup.mixed<number | ''>().default(''),
  margin_bonus_per: yup.mixed<number | ''>().default(''),
  margin_interest_month: yup.mixed<number | ''>().default(''),
  deposit_repayment_date: yup.string().default(''),

  is_intermediary: yup.boolean().default(false),
  shipper_code: yup.string().trim().default(''),
  contract_shipper_intermediary: yup.string().trim().default(''),
  outright_shipper_intermediary: yup.mixed<number | ''>().default(''),
  ptbfdiffs_shipper_intermediary: yup.mixed<number | ''>().default(''),

  is_issued: yup.boolean().default(false),
  is_sent_mail: yup.boolean().default(false),
  is_received_mail: yup.boolean().default(false),
  is_sent_paper: yup.boolean().default(false),
  is_received_paper: yup.boolean().default(false),
  note: yup.string().trim().default(''),
}) as yup.ObjectSchema<NPEFixationFormValues>

const TAB_LABELS = [
  'Fixation',
  'LocalP — General',
  'LocalP — Pricing',
  'LocalP — Logistics',
  'LocalP — Forward',
  'LocalP — Intermediary',
  'LocalP — Flags & Notes',
]

function valuesToLocalPCreateIn(v: NPEFixationFormValues): LocalPCreateIn {
  const num = (x: number | ''): number | null => (x === '' ? null : x)
  const str = (x: string): string | null => (x.trim() === '' ? null : x.trim())
  return {
    contract_no: str(v.contract_no),
    contract_date: v.contract_date,
    cp_code: v.cp_code.trim(),
    seller_ref: str(v.seller_ref),
    quality_code: str(v.quality_code),
    quality_des_contract: str(v.quality_des_contract),
    certification: str(v.certification),
    me_or_ib: v.me_or_ib === '' ? null : v.me_or_ib,
    license_cert_no: str(v.license_cert_no),
    quantity_kg: typeof v.quantity_kg === 'number' ? v.quantity_kg : 0,
    no_of_bag: num(v.no_of_bag),
    package: str(v.package),

    contract_type: v.contract_type,
    outright_price: num(v.outright_price),
    ptbf_diffs: num(v.ptbf_diffs),
    price_unit: str(v.price_unit),
    vat_rate: num(v.vat_rate),
    fx: num(v.fx),
    fixed_fx: num(v.fixed_fx),
    futures_month: str(v.futures_month),
    fixation_call: str(v.fixation_call),

    latest_delivery: str(v.latest_delivery),
    delivery_term: str(v.delivery_term),
    delivery_place_code: str(v.delivery_place_code),
    logistic_cost: num(v.logistic_cost),
    trader_in_charge: str(v.trader_in_charge),

    provi_market_rate: num(v.provi_market_rate),
    stoploss_rate: num(v.stoploss_rate),
    margin_value_per: num(v.margin_value_per),
    margin_limit_per: num(v.margin_limit_per),
    time_limit: num(v.time_limit),
    margin_bonus_per: num(v.margin_bonus_per),
    margin_interest_month: num(v.margin_interest_month),
    deposit_repayment_date: str(v.deposit_repayment_date),

    is_intermediary: v.is_intermediary,
    shipper_code: str(v.shipper_code),
    contract_shipper_intermediary: str(v.contract_shipper_intermediary),
    outright_shipper_intermediary: num(v.outright_shipper_intermediary),
    ptbfdiffs_shipper_intermediary: num(v.ptbfdiffs_shipper_intermediary),

    is_issued: v.is_issued,
    is_sent_mail: v.is_sent_mail,
    is_received_mail: v.is_received_mail,
    is_sent_paper: v.is_sent_paper,
    is_received_paper: v.is_received_paper,

    note: str(v.note),
  }
}

function valuesToCreateIn(v: NPEFixationFormValues): NPEFixationCreateIn {
  return {
    fixation: {
      npe_no: v.npe_no.trim(),
      fixation_date: v.fixation_date,
      fixation_price: typeof v.fixation_price === 'number' ? v.fixation_price : 0,
      note: v.fixation_note.trim() || null,
    },
    local_p: valuesToLocalPCreateIn(v),
  }
}

/** Auto-fill LocalP fields từ NPE contract khi user chọn NPE no. */
function applyNPEAutofill(
  setValue: ReturnType<typeof useForm<NPEFixationFormValues>>['setValue'],
  npe: NPEContractOut,
) {
  const set = (k: keyof NPEFixationFormValues, val: unknown) => {
    // @ts-expect-error setValue signature with dynamic key
    setValue(k, val ?? '', { shouldDirty: true })
  }
  set('cp_code', npe.cp_code ?? '')
  set('quality_code', npe.quality_code ?? '')
  set('certification', npe.certification ?? '')
  set('package', npe.package_code ?? '')
  set('latest_delivery', npe.delivery_time ?? '')
  set('delivery_place_code', npe.delivery_place_code ?? '')
  set('trader_in_charge', npe.trader_in_charge ?? '')
  set('shipper_code', npe.shipper_code ?? '')
  set('quantity_kg', npe.quantity_kg ?? '')
  set('stoploss_rate', npe.stoploss_rate ?? '')
  set('provi_market_rate', npe.provisional_market_rate ?? '')
  // shipper => intermediary
  set('is_intermediary', !!npe.shipper_code)
}

export interface NPEFixationFormDialogProps {
  open: boolean
  mode: 'create' | 'edit'
  initialValue?: NPEFixationOut | null
  /** Khi mở từ NPE detail tab, lock npe_no. */
  defaultNpeContractNo?: string
  onClose: () => void
  onSaved?: (id: number) => void
}

export function NPEFixationFormDialog({
  open,
  mode,
  initialValue,
  defaultNpeContractNo,
  onClose,
  onSaved,
}: NPEFixationFormDialogProps) {
  const snack = useSnackbar()
  const navigate = useNavigate()
  const isEdit = mode === 'edit'

  const [tab, setTab] = useState(0)

  const [createFix, { isLoading: creating }] = useCreateNPEFixationMutation()
  const [createAndAllocate, { isLoading: allocating }] =
    useCreateAndAllocateNPEFixationMutation()
  const [updateFix, { isLoading: updating }] = useUpdateNPEFixationMutation()
  const loading = creating || allocating || updating

  // NPE contracts cho autocomplete (page_size lớn)
  const { data: npeList } = useListNPEContractsQuery({ page: 1, page_size: 200 })
  const npeOptions = npeList?.items ?? []

  const {
    control,
    handleSubmit,
    reset,
    watch,
    setValue,
    getValues,
    formState: { errors },
  } = useForm<NPEFixationFormValues>({
    resolver: yupResolver(schema),
    defaultValues: EMPTY_FIX,
  })

  const npeNo = watch('npe_no')
  const contractType = watch('contract_type')
  const isIntermediary = watch('is_intermediary')
  const cpCode = watch('cp_code')
  const fixationPrice = watch('fixation_price')

  useEffect(() => {
    if (!open) return
    setTab(0)
    if (isEdit && initialValue) {
      // Khi edit fixation — chỉ reset header (BE detail trả supplier_name…
      // nhưng nested local_p chi tiết để FE đọc thêm nếu cần — Sprint 4 đơn giản).
      reset({
        ...EMPTY_FIX,
        npe_no: initialValue.contract_no ?? '',
        fixation_date: initialValue.fixation_date ?? dayjs().format('YYYY-MM-DD'),
        fixation_price: initialValue.price ?? '',
        fixation_note: initialValue.note ?? '',
        contract_no: initialValue.p_contract_no ?? '',
        cp_code: initialValue.cp_code ?? '',
        quantity_kg: initialValue.quantity_kg ?? '',
      })
    } else {
      reset({
        ...EMPTY_FIX,
        npe_no: defaultNpeContractNo ?? '',
      })
    }
  }, [open, isEdit, initialValue, defaultNpeContractNo, reset])

  // Khi npe_no thay đổi (create mode) -> auto-fill từ NPE detail.
  // Optimisation: dùng list (đã có cp_code, quantity, ...) để fill nhanh,
  // không cần round-trip detail (BE list trả đủ field cần).
  const [lastFilledNpe, setLastFilledNpe] = useState('')
  useEffect(() => {
    if (isEdit) return
    if (!npeNo || npeNo === lastFilledNpe) return
    const npe = npeOptions.find((c) => c.contract_no === npeNo)
    if (!npe) return
    applyNPEAutofill(setValue, npe)
    // Default contract_date = today nếu chưa có
    if (!getValues('contract_date')) {
      setValue('contract_date', dayjs().format('YYYY-MM-DD'))
    }
    setLastFilledNpe(npeNo)
  }, [npeNo, npeOptions, lastFilledNpe, isEdit, setValue, getValues])

  // Sync fixation_price -> outright_price (LocalP outright)
  useEffect(() => {
    if (typeof fixationPrice === 'number') {
      setValue('outright_price', fixationPrice, { shouldDirty: true })
    }
  }, [fixationPrice, setValue])

  // Reset state khi đóng
  useEffect(() => {
    if (!open) setLastFilledNpe('')
  }, [open])

  const submitWithMode = (mode: 'save' | 'save-allocate') =>
    handleSubmit(
      async (values) => {
        const payload = valuesToCreateIn(values)
        try {
          if (isEdit && initialValue) {
            const res = await updateFix({
              id: initialValue.cs_fixation_id,
              body: { fixation: payload.fixation },
            }).unwrap()
            snack.success(`Đã cập nhật Fixation ${initialValue.fixation_no}`)
            onSaved?.(res.data?.cs_fixation_id ?? initialValue.cs_fixation_id)
            onClose()
            return
          }
          if (mode === 'save-allocate') {
            const res = await createAndAllocate(payload).unwrap()
            const cs = res.data?.cs_fixation
            const sessionUid = res.data?.session_uid
            snack.success(`Đã tạo Fixation ${cs?.fixation_no ?? ''} — chuyển đến Allocation`)
            onSaved?.(cs?.cs_fixation_id ?? 0)
            onClose()
            if (cs?.cs_fixation_id && sessionUid) {
              navigate(
                `/purchase/npe-fixations/${cs.cs_fixation_id}/allocate?session=${encodeURIComponent(
                  sessionUid,
                )}`,
              )
            }
            return
          }
          const res = await createFix(payload).unwrap()
          snack.success(`Đã tạo Fixation ${res.data?.fixation_no ?? ''}`)
          onSaved?.(res.data?.cs_fixation_id ?? 0)
          onClose()
        } catch (err) {
          snack.error(
            getErrorMessage(
              err,
              isEdit
                ? 'Cập nhật Fixation thất bại'
                : mode === 'save-allocate'
                  ? 'Save & Allocate thất bại'
                  : 'Tạo Fixation thất bại',
            ),
          )
        }
      },
      () => {
        snack.error('Vui lòng kiểm tra các trường còn lỗi (xem các tab)')
      },
    )

  // Cast control sang LocalPFormValues để tái sử dụng các tab existing.
  const localPControl = control as unknown as Control<LocalPFormValues>
  const localPErrors = errors as unknown as import('react-hook-form').FieldErrors<LocalPFormValues>

  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
      <DialogTitle>
        {isEdit
          ? `Sửa Fixation ${initialValue?.fixation_no ?? ''}`
          : 'Tạo NPE Fixation'}
      </DialogTitle>
      <DialogContent dividers>
        <Tabs value={tab} onChange={(_e, v) => setTab(v)} variant="scrollable" scrollButtons="auto">
          {TAB_LABELS.map((l) => (
            <Tab key={l} label={l} />
          ))}
        </Tabs>

        <Box component="form" noValidate sx={{ pt: 2 }}>
          {tab === 0 && (
            <Box display="flex" flexDirection="column" gap={2} pt={1}>
              <Box display="flex" gap={2} flexWrap="wrap">
                <Controller
                  control={control}
                  name="npe_no"
                  render={({ field }) => {
                    const found = npeOptions.find((c) => c.contract_no === field.value)
                    const selected =
                      found ??
                      (field.value
                        ? ({
                            consignment_p_id: 0,
                            contract_no: field.value,
                            contract_date: null,
                            cp_code: null,
                            quantity_kg: null,
                          } as NPEContractOut)
                        : null)
                    return (
                      <Autocomplete
                        size="small"
                        sx={{ flex: '2 1 320px' }}
                        options={npeOptions}
                        value={selected}
                        onChange={(_e, v) => field.onChange(v?.contract_no ?? '')}
                        getOptionLabel={(opt) =>
                          opt
                            ? `${opt.contract_no} — ${opt.supplier_name ?? opt.cp_code ?? ''}`
                            : ''
                        }
                        isOptionEqualToValue={(o, v) => o.contract_no === v.contract_no}
                        disabled={isEdit || !!defaultNpeContractNo}
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            label="NPE Contract *"
                            error={!!errors.npe_no}
                            helperText={errors.npe_no?.message}
                          />
                        )}
                      />
                    )
                  }}
                />
                <TextField
                  label="Annex Index"
                  size="small"
                  value="Auto (PL01, PL02, ...)"
                  disabled
                  sx={{ flex: '1 1 200px' }}
                />
              </Box>

              <Box display="flex" gap={2} flexWrap="wrap">
                <Controller
                  control={control}
                  name="fixation_date"
                  render={({ field }) => (
                    <DatePicker
                      label="Ngày Fix *"
                      format="DD-MMM-YYYY"
                      value={field.value ? dayjs(field.value) : null}
                      onChange={(d) => field.onChange(d ? d.format('YYYY-MM-DD') : '')}
                      slotProps={{
                        textField: {
                          size: 'small',
                          sx: { flex: '1 1 200px' },
                          error: !!errors.fixation_date,
                          helperText: errors.fixation_date?.message,
                        },
                      }}
                    />
                  )}
                />
                <Controller
                  control={control}
                  name="fixation_price"
                  render={({ field }) => (
                    <TextField
                      {...field}
                      value={field.value === '' ? '' : field.value}
                      onChange={(e) =>
                        field.onChange(e.target.value === '' ? '' : Number(e.target.value))
                      }
                      label="Fixation Price *"
                      type="number"
                      size="small"
                      sx={{ flex: '1 1 200px' }}
                      error={!!errors.fixation_price}
                      helperText={
                        errors.fixation_price?.message ??
                        'Tự đồng bộ vào Outright Price của LocalP'
                      }
                    />
                  )}
                />
              </Box>

              <Controller
                control={control}
                name="fixation_note"
                render={({ field }) => (
                  <TextField
                    {...field}
                    label="Ghi chú Fixation"
                    size="small"
                    multiline
                    minRows={3}
                    fullWidth
                  />
                )}
              />
            </Box>
          )}

          {tab === 1 && (
            <GeneralTab
              control={localPControl}
              errors={localPErrors}
              isEdit={isEdit}
              cpCode={cpCode}
            />
          )}
          {tab === 2 && (
            <PricingTab
              control={localPControl}
              errors={localPErrors}
              contractType={contractType}
            />
          )}
          {tab === 3 && <LogisticsTab control={localPControl} errors={localPErrors} />}
          {tab === 4 && <ForwardTab control={localPControl} errors={localPErrors} />}
          {tab === 5 && (
            <IntermediaryTab
              control={localPControl}
              errors={localPErrors}
              isIntermediary={!!isIntermediary}
            />
          )}
          {tab === 6 && <FlagsNotesTab control={localPControl} errors={localPErrors} />}
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={loading}>
          Huỷ
        </Button>
        <Button
          onClick={() => void submitWithMode('save')()}
          variant="outlined"
          color="primary"
          disabled={loading}
          startIcon={creating ? <CircularProgress size={16} color="inherit" /> : undefined}
        >
          Save
        </Button>
        {!isEdit && (
          <Button
            onClick={() => void submitWithMode('save-allocate')()}
            variant="contained"
            color="warning"
            disabled={loading}
            startIcon={
              allocating ? <CircularProgress size={16} color="inherit" /> : undefined
            }
          >
            Save &amp; Allocate
          </Button>
        )}
      </DialogActions>
    </Dialog>
  )
}

export default NPEFixationFormDialog
